import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiChevronDown, FiPlus, FiTrash2, FiChevronUp, FiFileText, FiTruck, FiAlertCircle, FiClipboard, FiCopy, FiNavigation } from 'react-icons/fi';
import api from '../../services/api';
import ContainerTab from '../truck/ContainerTab';
import AccountingTab from '../truck/AccountingTab';
import DocCenterTab from '../truck/DocCenterTab';
import { getTrackingUrl } from '../../config/carrierTracking';
import StatusSelector from '../../components/common/StatusSelector';
import TradePartnerAutocomplete from '../../components/common/TradePartnerAutocomplete';

const OceanExportDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'Basic');
  const [hbls, setHbls] = useState([]);
  const [expandedHbls, setExpandedHbls] = useState({});
  const [showTools, setShowTools] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const initialLoadDone = useRef(false);
  const toolsRef = useRef(null);

  const emptyMbl = {
    file_no: '',
    mbl_no: '', office: '', bl_type: 'NORMAL', post_date: new Date().toISOString().split('T')[0],
    carrier_bkg_no: '', itn_no: '',
    carrier: '', bl_acct_carrier: '', shipping_agent: '', consignee: '', notify: '',
    forwarding_agent: '', co_loader: '', co: '', op: '',
    carrier_contract_no: '', direct_master: false,
    vessel: '', voyage: '', place_of_receipt: '', place_of_receipt_etd: '',
    port_of_loading: '', etd: '', port_of_discharge: '', eta: '',
    place_of_delivery: '', place_of_delivery_eta: '', final_destination: '', final_eta: '',
    empty_pickup: '', delivery_to_pier: '', pre_carriage_by: '', freight_location: '',
    atd: '', ata: '',
    freight: '', ship_mode: 'FCL', service_term_from: 'CY', service_term_to: 'CY',
    container_qty: '',
    obl_type: '', doc_cut_off_date: '', port_cut_off_date: '',
    vgm_cut_off_date: '', rail_cut_off_date: '',
    bl_cancelled: false, cancelled_by: '', reason_for_cancel: '',
    business_referred_by: '', booking_agent: false,
    freight_released: false, released_by: '',
    on_board_date: '', earliest_return_date: '',
    status: 'Pending', memo: '',
  };

  const [formData, setFormData] = useState(emptyMbl);
  const [shipmentId, setShipmentId] = useState(id || null);

  const dateFields = [
    'post_date','place_of_receipt_etd','etd','eta','place_of_delivery_eta','final_eta',
    'atd','ata','on_board_date','earliest_return_date',
  ];
  const datetimeFields = ['doc_cut_off_date','port_cut_off_date','vgm_cut_off_date','rail_cut_off_date'];

  useEffect(() => {
    if (id) { loadShipment(id); }
    else { setTimeout(() => { initialLoadDone.current = true; }, 0); }
  }, [id]);

  // Track dirty state from any formData change
  useEffect(() => {
    if (initialLoadDone.current) setIsDirty(true);
  }, [formData]);

  // Close tools dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) setShowTools(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Warn user about unsaved changes when leaving page
  useEffect(() => {
    if (!isNew && !isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isNew, isDirty]);

  const loadShipment = async (sid) => {
    try {
      const res = await api.get(`/ocean-export/${sid}`);
      const data = res.data;
      const mapped = {};
      Object.keys(emptyMbl).forEach(k => {
        if (dateFields.includes(k)) {
          mapped[k] = data[k] ? data[k].split('T')[0] : '';
        } else if (datetimeFields.includes(k)) {
          mapped[k] = data[k] || '';
        } else if (typeof emptyMbl[k] === 'boolean') {
          mapped[k] = !!data[k];
        } else {
          mapped[k] = data[k] || '';
        }
      });
      setFormData(mapped);
      setShipmentId(data.id);
      if (data.hbls && data.hbls.length > 0) {
        setHbls(data.hbls);
        const expanded = {};
        data.hbls.forEach(h => { expanded[h.id] = true; });
        setExpandedHbls(expanded);
      }
    } catch (error) {
      toast.error('Failed to load shipment');
      navigate('/ocean-export');
    } finally {
      setLoading(false);
      setTimeout(() => { initialLoadDone.current = true; }, 0);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setIsDirty(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        const { file_no, ...createData } = formData;
        const res = await api.post('/ocean-export', createData);
        const data = res.data;
        setShipmentId(data.id);
        setFormData(prev => ({ ...prev, file_no: data.file_no }));
        setIsDirty(false);
        toast.success(`Shipment ${data.file_no} created`);
        navigate(`/ocean-export/${data.id}`, { replace: true });
      } else {
        const { file_no, ...updateData } = formData;
        await api.put(`/ocean-export/${shipmentId}`, updateData);
        setIsDirty(false);
        toast.success('Shipment saved');
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ==================== HBL CRUD ====================
  const addHbl = async () => {
    try {
      const res = await api.post(`/ocean-export/${shipmentId}/hbl`, {});
      const newHbl = res.data;
      setHbls(prev => [...prev, newHbl]);
      setExpandedHbls(prev => ({ ...prev, [newHbl.id]: true }));
      toast.success('HB/L added');
    } catch (error) {
      toast.error('Failed to add HB/L');
    }
  };

  const handleHblChange = (hblId, e) => {
    const { name, value, type, checked } = e.target;
    setHbls(prev => prev.map(h =>
      h.id === hblId ? { ...h, [name]: type === 'checkbox' ? checked : value } : h
    ));
  };

  const setHblField = (hblId, name, value) => {
    setHbls(prev => prev.map(h =>
      h.id === hblId ? { ...h, [name]: value } : h
    ));
  };

  const saveHbl = async (hblId) => {
    const hbl = hbls.find(h => h.id === hblId);
    if (!hbl) return;
    try {
      const { id: _, shipment_id, created_at, updated_at, ...updateData } = hbl;
      await api.put(`/ocean-export/hbl/${hblId}`, updateData);
      toast.success('HB/L saved');
    } catch (error) {
      toast.error('Failed to save HB/L');
    }
  };

  const deleteHbl = async (hblId) => {
    if (!window.confirm('Delete this HB/L?')) return;
    try {
      await api.delete(`/ocean-export/hbl/${hblId}`);
      setHbls(prev => prev.filter(h => h.id !== hblId));
      toast.success('HB/L deleted');
    } catch (error) {
      toast.error('Failed to delete HB/L');
    }
  };

  const toggleHbl = (hblId) => {
    setExpandedHbls(prev => ({ ...prev, [hblId]: !prev[hblId] }));
  };

  const fmtDate = (d) => d ? d.split('T')[0] : '';

  if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;

  return (
    <div className="shipment-page">
      {/* Breadcrumb */}
      <div className="shipment-breadcrumb">
        <button className="breadcrumb-back-btn" onClick={() => {
          if ((isNew || isDirty) && !window.confirm('You are leaving the page without saving. Are you sure?')) return;
          navigate('/ocean-export');
        }} title="Back">
          <FiArrowLeft />
        </button>
        <span>Home</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span>Ocean Export</span>
        <span className="breadcrumb-sep">&gt;</span>
        <span className="breadcrumb-current">{formData.file_no || 'New'}</span>
      </div>

      {/* Tab Bar */}
      <div className="shipment-tabs">
        {['Basic', 'Container', 'Accounting', 'Doc Center'].map(tab => (
          <button key={tab} className={`shipment-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab}
          </button>
        ))}
      </div>

      {/* Header Summary Bar */}
      <div className="shipment-header-bar">
        <div className="header-left">
          <span className="header-mbl">
            <span className="mbl-label">MB/L</span>
            <span className="mbl-value">{formData.mbl_no || '-'}</span>
          </span>
          <span className="header-customer">
            <span className="customer-label">{formData.port_of_loading || '-'} ({formData.etd || '-'})</span>
            <span style={{ margin: '0 4px', color: '#94a3b8' }}>▸</span>
            <span className="customer-value">{formData.port_of_discharge || '-'} ({formData.eta || '-'})</span>
          </span>
        </div>
        <div className="header-right">
          <StatusSelector
            value={formData.status}
            options={['Pending', 'In Transit', 'Arrived', 'Delivered', 'Closed']}
            onChange={(val) => { setFormData(prev => ({ ...prev, status: val })); setIsDirty(true); }}
          />
          <span className="header-route">
            {formData.carrier || '-'} &middot; {formData.vessel || '-'}
          </span>
          {formData.mbl_no && (
            <button
              className="header-tools-btn"
              style={{ background: '#2563eb', color: '#fff', marginRight: '8px' }}
              onClick={() => {
                const tracking = getTrackingUrl(formData.vessel_flight_no || '', formData.mbl_no, 'ocean-export');
                if (tracking) window.open(tracking.url, '_blank');
              }}
              title="Track shipment online"
            >
              <FiNavigation size={14} style={{ marginRight: '4px' }} /> Track
            </button>
          )}
          <div ref={toolsRef} style={{ position: 'relative' }}>
            <button className="header-tools-btn" onClick={() => setShowTools(!showTools)}>
              Tools <FiChevronDown />
            </button>
            {showTools && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, zIndex: 200,
                background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: '220px',
                marginTop: '4px', overflow: 'hidden',
              }}>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>Documents</div>
                <button onClick={() => { setShowTools(false); window.open(`/documents/ocean-export/${shipmentId}/delivery-order`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiTruck size={15} style={{ color: '#2563eb' }} /> Pickup / Delivery Order
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/ocean-export/${shipmentId}/bill-of-lading`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiClipboard size={15} style={{ color: '#059669' }} /> Bill of Lading
                </button>
                <button onClick={() => { setShowTools(false); window.open(`/documents/ocean-export/${shipmentId}/arrival-notice`, '_blank'); }} style={toolsMenuItemStyle}>
                  <FiAlertCircle size={15} style={{ color: '#d97706' }} /> Arrival Notice
                </button>
                <div style={{ borderTop: '1px solid #f1f5f9', margin: '2px 0' }}></div>
                <div style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Actions</div>
                <button onClick={async () => {
                  setShowTools(false);
                  if (!window.confirm('Copy this shipment? A new shipment will be created with same customer, delivery info, and charges (MBL/HBL/Container left blank).')) return;
                  try {
                    const res = await api.post(`/ocean-export/${shipmentId}/copy`);
                    toast.success(`Shipment copied! New File No: ${res.data.file_no}`);
                    navigate(`/ocean-export/${res.data.id}`);
                  } catch (err) {
                    toast.error('Failed to copy shipment');
                  }
                }} style={toolsMenuItemStyle}>
                  <FiCopy size={15} style={{ color: '#6366f1' }} /> Copy Shipment
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ==================== BASIC TAB ==================== */}
      {activeTab === 'Basic' && (
        <div className="oi-basic-content">
          <form onSubmit={handleSave} className="shipment-form">
            <div className="oi-mbl-section">

              {/* General */}
              <div className="form-section-header">General Information</div>
              <div className="shipment-form-grid">
                <FG label="File No." value={formData.file_no} disabled />
                <FG label="MB/L No." name="mbl_no" value={formData.mbl_no} onChange={handleChange} required />
                <FGSelect label="B/L Type" name="bl_type" value={formData.bl_type} onChange={handleChange}
                  options={[['NORMAL','NORMAL'],['SEAWAY BILL','SEAWAY BILL'],['EXPRESS','EXPRESS']]} />
                <FGDate label="Post Date" name="post_date" value={formData.post_date} onChange={handleChange} />
                <FG label="Carrier Bkg. No." name="carrier_bkg_no" value={formData.carrier_bkg_no} onChange={handleChange} />
                <FG label="ITN No." name="itn_no" value={formData.itn_no} onChange={handleChange} />
                <FG label="Carrier" name="carrier" value={formData.carrier} onChange={handleChange} />
                <FG label="B/L Acct. Carrier" name="bl_acct_carrier" value={formData.bl_acct_carrier} onChange={handleChange} />
                <FG label="Shipping Agent" name="shipping_agent" value={formData.shipping_agent} onChange={handleChange} />
                <div className="shipment-form-group"><label>(Oversea Agent) Consignee</label>
                  <TradePartnerAutocomplete value={formData.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                    onChange={(val) => setFormData(prev => ({ ...prev, consignee: val }))} /></div>
                <div className="shipment-form-group"><label>Notify</label>
                  <TradePartnerAutocomplete value={formData.notify} tpTypes="Customer,Vendor" placeholder="Search notify..."
                    onChange={(val) => setFormData(prev => ({ ...prev, notify: val }))} /></div>
                <div className="shipment-form-group"><label>Forwarding Agent</label>
                  <TradePartnerAutocomplete value={formData.forwarding_agent} tpTypes="Customer,Vendor" placeholder="Search forwarding agent..."
                    onChange={(val) => setFormData(prev => ({ ...prev, forwarding_agent: val }))} /></div>
                <FG label="Co-loader" name="co_loader" value={formData.co_loader} onChange={handleChange} />
                <FG label="C/O" name="co" value={formData.co} onChange={handleChange} />
                <FG label="OP" name="op" value={formData.op} onChange={handleChange} />
                <FG label="Carrier Contract No." name="carrier_contract_no" value={formData.carrier_contract_no} onChange={handleChange} />
                <FGCheck label="Direct Master" name="direct_master" checked={formData.direct_master} onChange={handleChange} />
              </div>

              {/* Vessel & Route */}
              <div className="form-section-header">Vessel & Route</div>
              <div className="shipment-form-grid">
                <FG label="Vessel" name="vessel" value={formData.vessel} onChange={handleChange} />
                <FG label="Voyage" name="voyage" value={formData.voyage} onChange={handleChange} />
                <FG label="Place of Receipt" name="place_of_receipt" value={formData.place_of_receipt} onChange={handleChange} />
                <FGDate label="Place of Receipt ETD" name="place_of_receipt_etd" value={formData.place_of_receipt_etd} onChange={handleChange} />
                <div className="shipment-form-group"><label>Port of Loading</label>
                  <TradePartnerAutocomplete value={formData.port_of_loading} placeholder="Port of loading..."
                    onChange={(val) => setFormData(prev => ({ ...prev, port_of_loading: val }))} /></div>
                <FGDate label="ETD" name="etd" value={formData.etd} onChange={handleChange} required />
                <div className="shipment-form-group"><label>Port of Discharge</label>
                  <TradePartnerAutocomplete value={formData.port_of_discharge} placeholder="Port of discharge..."
                    onChange={(val) => setFormData(prev => ({ ...prev, port_of_discharge: val }))} /></div>
                <FGDate label="ETA" name="eta" value={formData.eta} onChange={handleChange} />
                <div className="shipment-form-group"><label>Place of Delivery (DEL)</label>
                  <TradePartnerAutocomplete value={formData.place_of_delivery} placeholder="Place of delivery..."
                    onChange={(val) => setFormData(prev => ({ ...prev, place_of_delivery: val }))} /></div>
                <FGDate label="Place of Delivery ETA" name="place_of_delivery_eta" value={formData.place_of_delivery_eta} onChange={handleChange} />
                <div className="shipment-form-group"><label>Final Destination</label>
                  <TradePartnerAutocomplete value={formData.final_destination} placeholder="Final destination..."
                    onChange={(val) => setFormData(prev => ({ ...prev, final_destination: val }))} /></div>
                <FGDate label="Final ETA" name="final_eta" value={formData.final_eta} onChange={handleChange} />
                <div className="shipment-form-group"><label>Empty Pickup</label>
                  <TradePartnerAutocomplete value={formData.empty_pickup} placeholder="Empty pickup..."
                    onChange={(val) => setFormData(prev => ({ ...prev, empty_pickup: val }))} /></div>
                <div className="shipment-form-group"><label>Delivery To/Pier</label>
                  <TradePartnerAutocomplete value={formData.delivery_to_pier} placeholder="Delivery to/pier..."
                    onChange={(val) => setFormData(prev => ({ ...prev, delivery_to_pier: val }))} /></div>
                <FG label="Pre-carriage by" name="pre_carriage_by" value={formData.pre_carriage_by} onChange={handleChange} />
                <div className="shipment-form-group"><label>Freight Location</label>
                  <TradePartnerAutocomplete value={formData.freight_location} placeholder="Freight location..."
                    onChange={(val) => setFormData(prev => ({ ...prev, freight_location: val }))} /></div>
                <FGDate label="ATD" name="atd" value={formData.atd} onChange={handleChange} />
                <FGDate label="ATA" name="ata" value={formData.ata} onChange={handleChange} />
              </div>

              {/* Freight & Shipping */}
              <div className="form-section-header">Freight & Shipping</div>
              <div className="shipment-form-grid">
                <FGSelect label="Freight" name="freight" value={formData.freight} onChange={handleChange}
                  options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                <FGSelect label="Ship Mode" name="ship_mode" value={formData.ship_mode} onChange={handleChange}
                  options={[['FCL','FCL'],['LCL','LCL']]} />
                <div className="shipment-form-group">
                  <label>SVC Term</label>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <select name="service_term_from" value={formData.service_term_from} onChange={handleChange} style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', height: '34px' }}>
                      <option value="CY">CY</option><option value="CFS">CFS</option><option value="DOOR">DOOR</option>
                    </select>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>~</span>
                    <select name="service_term_to" value={formData.service_term_to} onChange={handleChange} style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', height: '34px' }}>
                      <option value="CY">CY</option><option value="CFS">CFS</option><option value="DOOR">DOOR</option>
                    </select>
                  </div>
                </div>
                <FG label="Container/Qty" name="container_qty" value={formData.container_qty} onChange={handleChange} placeholder="e.g. 40HC*1" />
              </div>

              {/* OB/L & Cut-Off */}
              <div className="form-section-header">OB/L & Cut-Off Dates</div>
              <div className="shipment-form-grid">
                <FGSelect label="OB/L Type" name="obl_type" value={formData.obl_type} onChange={handleChange}
                  options={[['','--'],['ORIGINAL','ORIGINAL'],['SEAWAY BILL','SEAWAY BILL'],['TELEX RELEASE','TELEX RELEASE'],['EXPRESS','EXPRESS']]} />
                <FG label="Doc Cut-Off Date" name="doc_cut_off_date" value={formData.doc_cut_off_date} onChange={handleChange} type="datetime-local" />
                <FG label="Port Cut-Off Date" name="port_cut_off_date" value={formData.port_cut_off_date} onChange={handleChange} type="datetime-local" />
                <FG label="VGM Cut-Off Date" name="vgm_cut_off_date" value={formData.vgm_cut_off_date} onChange={handleChange} type="datetime-local" />
                <FG label="Rail Cut-Off Date" name="rail_cut_off_date" value={formData.rail_cut_off_date} onChange={handleChange} type="datetime-local" />
              </div>

              {/* Cancel & Release */}
              <div className="form-section-header">Release & Status</div>
              <div className="shipment-form-grid">
                <FGCheck label="BL Cancelled" name="bl_cancelled" checked={formData.bl_cancelled} onChange={handleChange} />
                <FG label="Cancelled By" name="cancelled_by" value={formData.cancelled_by} onChange={handleChange} />
                <FGSelect label="Reason for Cancel" name="reason_for_cancel" value={formData.reason_for_cancel} onChange={handleChange}
                  options={[['','--'],['CUSTOMER REQUEST','CUSTOMER REQUEST'],['BOOKING CHANGE','BOOKING CHANGE'],['OTHER','OTHER']]} />
                <FG label="Business Referred By" name="business_referred_by" value={formData.business_referred_by} onChange={handleChange} />
                <FGCheck label="Booking Agent" name="booking_agent" checked={formData.booking_agent} onChange={handleChange} />
                <FGCheck label="Freight Released" name="freight_released" checked={formData.freight_released} onChange={handleChange} />
                <FG label="Released By" name="released_by" value={formData.released_by} onChange={handleChange} />
                <FGDate label="On Board Date" name="on_board_date" value={formData.on_board_date} onChange={handleChange} />
                <FGDate label="Earliest Return Date" name="earliest_return_date" value={formData.earliest_return_date} onChange={handleChange} />
                <FGSelect label="Status" name="status" value={formData.status} onChange={handleChange}
                  options={[['Pending','Pending'],['In Transit','In Transit'],['Arrived','Arrived'],['Delivered','Delivered'],['Closed','Closed']]} />
              </div>

              {/* Memo */}
              <div className="form-section-header">Memo</div>
              <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                <div className="shipment-form-group">
                  <textarea name="memo" value={formData.memo} onChange={handleChange} rows={3}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }} />
                </div>
              </div>

              <div className="shipment-form-actions">
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : 'Save MBL'}
                </button>
              </div>
            </div>

            {/* ==================== HBL Section ==================== */}
            <div className="oi-hbl-section">
              <div className="oi-hbl-header">
                <span className="oi-hbl-title">House B/L ({hbls.length})</span>
                <button type="button" className="btn-add-hbl" onClick={addHbl} disabled={isNew} title={isNew ? 'Save shipment first' : ''}>
                  <FiPlus size={13} /> Add HB/L
                </button>
              </div>

              {hbls.length === 0 && (
                <div className="oi-hbl-empty">No House B/L added yet. Click "+ Add HB/L" to create one.</div>
              )}

              {hbls.map((hbl) => (
                <div className="oi-hbl-card" key={hbl.id}>
                  <div className="oi-hbl-card-header" onClick={() => toggleHbl(hbl.id)}>
                    <div className="oi-hbl-card-info">
                      <span className="oi-hbl-no">{hbl.hbl_no || '(No HB/L No.)'}</span>
                      <span className="oi-hbl-customer">{hbl.customer || ''}</span>
                      {hbl.op && <span className="oi-hbl-op">OP: {hbl.op}</span>}
                      <span className={`status-badge status-${(hbl.status || 'pending').toLowerCase()}`}>
                        {hbl.status || 'Pending'}
                      </span>
                    </div>
                    <div className="oi-hbl-card-actions">
                      <button type="button" className="btn-delete-hbl" onClick={(e) => { e.stopPropagation(); deleteHbl(hbl.id); }} title="Delete HB/L">
                        <FiTrash2 size={13} />
                      </button>
                      {expandedHbls[hbl.id] ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                    </div>
                  </div>

                  {expandedHbls[hbl.id] && (
                    <div className="oi-hbl-card-body">

                      {/* General */}
                      <div className="form-section-header">General</div>
                      <div className="shipment-form-grid">
                        <HF label="HB/L No." name="hbl_no" hbl={hbl} onChange={handleHblChange} required />
                        <HF label="Customer Ref. No." name="customer_ref_no" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Booking No." name="booking_no" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Document No." name="document_no" hbl={hbl} onChange={handleHblChange} />
                        <HF label="ITN No." name="itn_no" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Quotation No." name="quotation_no" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Actual Shipper" name="actual_shipper" hbl={hbl} onChange={handleHblChange} />
                        <div className="shipment-form-group"><label>Customer</label>
                          <TradePartnerAutocomplete value={hbl.customer} placeholder="Search customer..."
                            onChange={(val) => setHblField(hbl.id, 'customer', val)} /></div>
                        <div className="shipment-form-group"><label>Bill To</label>
                          <TradePartnerAutocomplete value={hbl.bill_to} placeholder="Search bill to..."
                            onChange={(val) => setHblField(hbl.id, 'bill_to', val)} /></div>
                        <div className="shipment-form-group"><label>Consignee</label>
                          <TradePartnerAutocomplete value={hbl.consignee} tpTypes="Customer,Vendor" placeholder="Search consignee..."
                            onChange={(val) => setHblField(hbl.id, 'consignee', val)} /></div>
                        <div className="shipment-form-group"><label>Notify</label>
                          <TradePartnerAutocomplete value={hbl.notify} tpTypes="Customer,Vendor" placeholder="Search notify..."
                            onChange={(val) => setHblField(hbl.id, 'notify', val)} /></div>
                        <HF label="Also Notify" name="also_notify" hbl={hbl} onChange={handleHblChange} />
                        <div className="shipment-form-group"><label>Customs Broker</label>
                          <TradePartnerAutocomplete value={hbl.customs_broker} tpTypes="Customer,Vendor" placeholder="Search customs broker..."
                            onChange={(val) => setHblField(hbl.id, 'customs_broker', val)} /></div>
                        <div className="shipment-form-group"><label>Trucker</label>
                          <TradePartnerAutocomplete value={hbl.trucker} tpTypes="Trucker" placeholder="Search trucker..."
                            onChange={(val) => setHblField(hbl.id, 'trucker', val)} /></div>
                        <div className="shipment-form-group"><label>Forwarding Agent</label>
                          <TradePartnerAutocomplete value={hbl.forwarding_agent} tpTypes="Customer,Vendor" placeholder="Search forwarding agent..."
                            onChange={(val) => setHblField(hbl.id, 'forwarding_agent', val)} /></div>
                        <HF label="HB/L Agent" name="hbl_agent" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Sales" name="sales" hbl={hbl} onChange={handleHblChange} />
                        <HF label="OP" name="op" hbl={hbl} onChange={handleHblChange} />
                        <HF label="MRN No." name="mrn_no" hbl={hbl} onChange={handleHblChange} />
                        <HFCheck label="Sub Agent B/L" name="sub_agent_bl" hbl={hbl} onChange={handleHblChange} />
                      </div>

                      {/* Route & Delivery */}
                      <div className="form-section-header">Route & Delivery</div>
                      <div className="shipment-form-grid">
                        <HF label="Place of Receipt" name="place_of_receipt" hbl={hbl} onChange={handleHblChange} />
                        <div className="shipment-form-group"><label>Place of Delivery (DEL)</label>
                          <TradePartnerAutocomplete value={hbl.place_of_delivery} placeholder="Place of delivery..."
                            onChange={(val) => setHblField(hbl.id, 'place_of_delivery', val)} /></div>
                        <HFDate label="Place of Delivery ETA" name="place_of_delivery_eta" hbl={hbl} onChange={handleHblChange} />
                        <HF label="FBA FC" name="fba_fc" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Cargo Pickup" name="cargo_pickup" hbl={hbl} onChange={handleHblChange} />
                        <div className="shipment-form-group"><label>Port of Discharge</label>
                          <TradePartnerAutocomplete value={hbl.port_of_discharge} placeholder="Port of discharge..."
                            onChange={(val) => setHblField(hbl.id, 'port_of_discharge', val)} /></div>
                        <div className="shipment-form-group"><label>Final Destination</label>
                          <TradePartnerAutocomplete value={hbl.final_destination} placeholder="Final destination..."
                            onChange={(val) => setHblField(hbl.id, 'final_destination', val)} /></div>
                        <HFDate label="Final ETA" name="final_eta" hbl={hbl} onChange={handleHblChange} />
                        <div className="shipment-form-group"><label>Empty Pickup</label>
                          <TradePartnerAutocomplete value={hbl.empty_pickup} placeholder="Empty pickup..."
                            onChange={(val) => setHblField(hbl.id, 'empty_pickup', val)} /></div>
                        <div className="shipment-form-group"><label>Delivery To/Pier</label>
                          <TradePartnerAutocomplete value={hbl.delivery_to_pier} placeholder="Delivery to/pier..."
                            onChange={(val) => setHblField(hbl.id, 'delivery_to_pier', val)} /></div>
                        <HFDate label="ETA" name="eta" hbl={hbl} onChange={handleHblChange} />
                        <HFDate label="Cargo Ready Date" name="cargo_ready_date" hbl={hbl} onChange={handleHblChange} />
                      </div>

                      {/* Freight & Shipping */}
                      <div className="form-section-header">Freight & Shipping</div>
                      <div className="shipment-form-grid">
                        <div className="shipment-form-group">
                          <label>Service Term</label>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <select name="service_term_from" value={hbl.service_term_from || 'CY'} onChange={(e) => handleHblChange(hbl.id, e)} style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', height: '34px' }}>
                              <option value="CY">CY</option><option value="CFS">CFS</option><option value="DOOR">DOOR</option>
                            </select>
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>~</span>
                            <select name="service_term_to" value={hbl.service_term_to || 'CY'} onChange={(e) => handleHblChange(hbl.id, e)} style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', height: '34px' }}>
                              <option value="CY">CY</option><option value="CFS">CFS</option><option value="DOOR">DOOR</option>
                            </select>
                          </div>
                        </div>
                        <HFSelect label="Ship Mode" name="ship_mode" hbl={hbl} onChange={handleHblChange} options={[['FCL','FCL'],['LCL','LCL']]} />
                        <HF label="Container/Qty" name="container_qty" hbl={hbl} onChange={handleHblChange} />
                        <HFSelect label="Buying Freight" name="buying_freight" hbl={hbl} onChange={handleHblChange} options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                        <HFSelect label="Selling Freight" name="selling_freight" hbl={hbl} onChange={handleHblChange} options={[['','--'],['PREPAID','PREPAID'],['COLLECT','COLLECT']]} />
                      </div>

                      {/* Sales & Terms */}
                      <div className="form-section-header">Sales & Terms</div>
                      <div className="shipment-form-grid">
                        <HFRadio label="Express B/L" name="express_bl" hbl={hbl} onChange={handleHblChange} />
                        <HFSelect label="Cargo Type" name="cargo_type" hbl={hbl} onChange={handleHblChange}
                          options={[['GENERAL CARGO','GENERAL CARGO'],['HAZ','HAZ'],['REEFER','REEFER']]} />
                        <HF label="Sales Type" name="sales_type" hbl={hbl} onChange={handleHblChange} />
                        <HF label="W/H Cut-Off Date" name="wh_cut_off_date" hbl={hbl} onChange={handleHblChange} type="datetime-local" />
                        <HFDate label="Earliest Return Date" name="earliest_return_date" hbl={hbl} onChange={handleHblChange} />
                        <HF label="L/C No." name="lc_no" hbl={hbl} onChange={handleHblChange} />
                        <HF label="L/C Issue Bank" name="lc_issue_bank" hbl={hbl} onChange={handleHblChange} />
                        <HFDate label="L/C Issue Date" name="lc_issue_date" hbl={hbl} onChange={handleHblChange} />
                        <HFDate label="On Board Date" name="on_board_date" hbl={hbl} onChange={handleHblChange} />
                        <HFRadio label="Stackable" name="stackable" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Incoterms" name="incoterms" hbl={hbl} onChange={handleHblChange} />
                      </div>

                      {/* Release & Cancel */}
                      <div className="form-section-header">Release & Cancel</div>
                      <div className="shipment-form-grid">
                        <HFCheck label="Hold" name="hold" hbl={hbl} onChange={handleHblChange} />
                        <HFCheck label="Freight Released" name="freight_released" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Released By" name="released_by" hbl={hbl} onChange={handleHblChange} />
                        <HFCheck label="BL Cancelled" name="bl_cancelled" hbl={hbl} onChange={handleHblChange} />
                        <HF label="Cancelled By" name="cancelled_by" hbl={hbl} onChange={handleHblChange} />
                        <HFSelect label="Reason for Cancel" name="reason_for_cancel" hbl={hbl} onChange={handleHblChange}
                          options={[['','--'],['CUSTOMER REQUEST','CUSTOMER REQUEST'],['BOOKING CHANGE','BOOKING CHANGE'],['OTHER','OTHER']]} />
                        <HF label="Business Referred By" name="business_referred_by" hbl={hbl} onChange={handleHblChange} />
                      </div>

                      {/* Cargo */}
                      <div className="form-section-header">Cargo</div>
                      <div className="shipment-form-grid">
                        <HF label="Package Count" name="package_count" hbl={hbl} onChange={handleHblChange} type="number" />
                        <HFSelect label="PKG Type" name="pkg_type" hbl={hbl} onChange={handleHblChange}
                          options={[['CARTON(S)','CARTON(S)'],['PALLET(S)','PALLET(S)'],['PACKAGE(S)','PACKAGE(S)'],['PIECE(S)','PIECE(S)'],['DRUM(S)','DRUM(S)']]} />
                        <HF label="Weight (KG)" name="weight_kg" hbl={hbl} onChange={handleHblChange} type="number" />
                        <HF label="Measurement (CBM)" name="measurement_cbm" hbl={hbl} onChange={handleHblChange} type="number" />
                      </div>

                      {/* Memo */}
                      <div className="form-section-header">Memo</div>
                      <div className="shipment-form-grid" style={{ gridTemplateColumns: '1fr' }}>
                        <div className="shipment-form-group">
                          <textarea name="memo" value={hbl.memo || ''} onChange={(e) => handleHblChange(hbl.id, e)} rows={2}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', color: '#1e293b' }} />
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 0' }}>
                        <button type="button" className="btn-save" onClick={() => saveHbl(hbl.id)}>Save HB/L</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </form>
        </div>
      )}

      {activeTab === 'Container' && shipmentId && <ContainerTab shipmentId={shipmentId} shipmentType="ocean-export" />}
      {activeTab === 'Container' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to manage containers.</div>}
      {activeTab === 'Accounting' && shipmentId && <AccountingTab shipmentId={shipmentId} shipmentType="ocean_export" />}
      {activeTab === 'Accounting' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to manage accounting.</div>}
      {activeTab === 'Doc Center' && shipmentId && <DocCenterTab shipmentId={shipmentId} shipmentType="ocean-export" />}
      {activeTab === 'Doc Center' && !shipmentId && <div style={{ padding: '24px', color: '#94a3b8', textAlign: 'center' }}>Please save the shipment first to access documents.</div>}
    </div>
  );
};

// ==================== Helper Components ====================
const FG = ({ label, name, value, onChange, required, disabled, type = 'text', placeholder }) => (
  <div className="shipment-form-group">
    <label className={required ? 'required' : ''}>{label}</label>
    <input type={type} name={name} value={value || ''} onChange={onChange} disabled={disabled} placeholder={placeholder} />
  </div>
);
const FGDate = ({ label, name, value, onChange, required }) => (
  <div className="shipment-form-group">
    <label className={required ? 'required' : ''}>{label}</label>
    <input type="date" name={name} value={value || ''} onChange={onChange} />
  </div>
);
const FGCheck = ({ label, name, checked, onChange }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <div style={{ paddingTop: '6px' }}><input type="checkbox" name={name} checked={checked} onChange={onChange} /></div>
  </div>
);
const FGSelect = ({ label, name, value, onChange, options }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <select name={name} value={value || ''} onChange={onChange}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

// HBL field helpers
const HF = ({ label, name, hbl, onChange, required, type = 'text' }) => (
  <div className="shipment-form-group">
    <label className={required ? 'required' : ''}>{label}</label>
    <input type={type} name={name} value={hbl[name] || ''} onChange={(e) => onChange(hbl.id, e)} />
  </div>
);
const HFDate = ({ label, name, hbl, onChange }) => {
  const v = hbl[name] ? hbl[name].split('T')[0] : '';
  return (
    <div className="shipment-form-group">
      <label>{label}</label>
      <input type="date" name={name} value={v} onChange={(e) => onChange(hbl.id, e)} />
    </div>
  );
};
const HFCheck = ({ label, name, hbl, onChange }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <div style={{ paddingTop: '6px' }}><input type="checkbox" name={name} checked={!!hbl[name]} onChange={(e) => onChange(hbl.id, e)} /></div>
  </div>
);
const HFSelect = ({ label, name, hbl, onChange, options }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <select name={name} value={hbl[name] || ''} onChange={(e) => onChange(hbl.id, e)}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);
const HFRadio = ({ label, name, hbl, onChange }) => (
  <div className="shipment-form-group">
    <label>{label}</label>
    <div style={{ paddingTop: '6px', display: 'flex', gap: '12px', fontSize: '13px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
        <input type="radio" name={name} value="1" checked={!!hbl[name]} onChange={(e) => onChange(hbl.id, { target: { name, value: true, type: 'radio' } })} /> Yes
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
        <input type="radio" name={name} value="0" checked={!hbl[name]} onChange={(e) => onChange(hbl.id, { target: { name, value: false, type: 'radio' } })} /> No
      </label>
    </div>
  </div>
);

const toolsMenuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  width: '100%', padding: '10px 14px', border: 'none',
  background: 'transparent', cursor: 'pointer', fontSize: '13px',
  fontWeight: 500, color: '#334155', textAlign: 'left',
  transition: 'background 0.15s',
};

export default OceanExportDetail;
